import { z } from 'zod';
import { regelkennungSchema } from './kontierung.js';
import { isoDate } from './posting.js';
/**
 * `GET /api/v1/regeln` — die Lesesicht über das Regelregister. Auftrag
 * `W-regeluebersicht` (2026-08-31), Stufe 1 des Regel-UI-Wunschs.
 *
 * **Reine Lesesicht, kein Anlegen.** Regeln sind Code (Entscheidung `REI-2 =
 * B`, `apps/api/src/regeln/register.ts`) — sie entstehen als geprüfte
 * Aufträge und nicht über ein Formular dieser Seite. Was hier steht, ist der
 * Vertrag der ANTWORT, nicht ein Vertrag zum Anlegen oder Ändern einer Regel.
 *
 * **Zwei Quellen, eine Zeile je Regel.** `apps/api/src/regeln/register.ts`
 * (Code, zur Laufzeit gelesen — kein zweiter Abschrieb) liefert Kennung,
 * Klartext, Aktivierungsgrenze, Altbestands- und Ausnahmeangabe;
 * `apps/api/src/regeln/uebersicht.query.ts` misst dazu drei ehrlich
 * abfragbare Zählungen (siehe `zaehlungSchema` unten). Was NICHT ehrlich
 * abfragbar ist — ob die Regel fachlich richtig entscheidet — steht hier
 * nicht; das prüfen die Durchsetzungsfälle je Regel (`E-498`–`E-501`,
 * `E-528`–`E-531` und Geschwister).
 */
/**
 * **Die drei Zählungen, die sich ohne Erfindung aus der Datenbank ziehen
 * lassen** — gemessen am Bestand von `R-regelregister`/`R-regeln234`/
 * `R-altbestand2` (2026-08-29).
 *
 * `gebuchtLaufend` — `count(*)` über `kontierung` mit
 * `herkunft = <spur.herkunft der Regel>` und `freigegeben_am IS NOT NULL`.
 * Derselbe Zählweg wie das `gesamt`-Feld von
 * `GET /api/v1/kontierungen/automatisch`, hier nur je Regel statt gekappt auf
 * ein Fenster.
 *
 * `gebuchtAltbestand` — dieselbe Zählung für die ZWEITE Herkunftsmarke einer
 * Regel (`Registerregel.herkuenfte[1]`), sofern es sie gibt — die Marke eines
 * bereits gefahrenen Buchungs-Nachlaufs (Regel 1: `ALTBESTAND_HERKUNFT`, aus
 * `R-altbestand`). `0`, wenn die Regel keine zweite Marke führt.
 *
 * `vermerkt` — `count(*)` über `audit_log` mit
 * `action = 'bank.altumbuchung.vermerkt'` und `payload->>'regel' = <Kennung>`
 * — die Zeilen, die `R-altbestand2` statt zu buchen VERMERKT hat
 * (`match_status = 'ignored'`, Kopfkommentar `apps/api/src/regeln/altbestand2.ts`,
 * Abschnitt „Die Antwort ist deshalb: VERMERKEN statt buchen"). Diese Zählung
 * ist unabhängig von den zwei Herkunftsmarken oben — ein Vermerk ändert
 * `kontierung.herkunft` nicht, er ändert `bank_transaction.match_status`.
 * `0` heisst ausdrücklich „keine einzige Vermerkzeile gefunden" und NICHT
 * „nicht anwendbar" — für Regeln ohne Altbestand2-Nachlauf (1 und 3) ist das
 * die korrekte, ehrliche Antwort.
 */
export const regelZaehlungSchema = z
    .object({
    gebuchtLaufend: z.number().int().nonnegative(),
    gebuchtAltbestand: z.number().int().nonnegative(),
    vermerkt: z.number().int().nonnegative(),
})
    .strict();
/**
 * Eine Zeile der Übersicht — eine Regel des Registers samt ihren Angaben und
 * Zählungen.
 *
 * **Die Felder sind Klartext aus dem Register und keine zweite Übersetzung.**
 * `altbestand` und `ausnahmen` kommen wörtlich aus
 * `Registerregel.altbestand`/`Registerregel.ausnahmen` — sie sind bereits die
 * ehrlichen, für einen Leser geschriebenen Sätze (siehe deren Kopfkommentare
 * in `register.ts`), eine Umformulierung hier wäre eine zweite, drift-fähige
 * Fassung derselben Aussage.
 *
 * **Drei Felder sind Entwicklungswissen und fehlen in der Auslieferung**
 * (`K-regelregisterkunde`, 2026-10-15, `E-1907`): `altbestand`, `ausnahmen`
 * und `quelle` tragen Projektgeschichte (Auftragskennungen, Messungen an der
 * Buchhaltung des Herstellers, Repository-Pfade) und gehören nicht in ein
 * Kundenprodukt. Die API liefert sie nur in der Bauart `entwicklung`; in der
 * Auslieferung fehlen die Schlüssel im Antwortkörper — sie sind nicht leer,
 * sondern nicht da. `ausnahmen: []` heisst deshalb „Entwicklungsansicht, keine
 * Ausnahme“ und nie „unbekannt“.
 */
export const regelUebersichtZeileSchema = z
    .object({
    kennung: regelkennungSchema,
    titel: z.string(),
    herkunft: z.string(),
    greiftSeit: isoDate,
    gegenkonto: z.string(),
    altbestand: z.string().optional(),
    ausnahmen: z.array(z.string()).optional(),
    nachschauEndpunkt: z.string(),
    nachschauAbschnitt: z.string(),
    quelle: z.string().optional(),
    zaehlung: regelZaehlungSchema,
})
    .strict();
/**
 * Die ganze Antwort — jede Regel des Registers genau einmal, in
 * Registerreihenfolge.
 */
export const regelUebersichtSchema = z
    .object({
    regeln: z.array(regelUebersichtZeileSchema),
})
    .strict();
//# sourceMappingURL=regeln.js.map